import { PurchaseOrderBundle } from '@/services/api/contracts/catalog/bundles';
import { useProducts } from '@/services/api/hooks/product';
import { PoStatus } from '@/services/purchaseOrders/usePurchaseOrderService';
import { useDeviceFeeById } from '@/utils/deviceFee';
import { useMemo } from 'react';

export const usePurchaseOrderDetails = (order?: PurchaseOrderBundle) => {
  const [products] = useProducts(order?.submittedOn ? new Date(order.submittedOn) : undefined);

  const models = order?.financeModels || [];
  const perModel = order?.peripheralFinanceModel;

  const deviceFee = useDeviceFeeById(
    order?.deviceFeeCatalogItem?.id,
    order?.submittedOn ? new Date(order.submittedOn) : undefined
  );

  const bundledItems = order ? order.bundledItems : [];
  const bundledItemIds = bundledItems.map(item => item.catalogItem);

  const poProducts = products
    .filter(product => bundledItemIds.includes(product.id))
    .sort((a, b) => bundledItemIds.indexOf(a.id) - bundledItemIds.indexOf(b.id));

  const productNameString = useMemo(() => {
    let string = '';
    let stringFinished = false;

    bundledItems.forEach((selectedProduct, index) => {
      if (stringFinished) {
        return;
      }

      const product = products.find(p => p.id === selectedProduct.catalogItem);

      let productName = 'Unknown Product';
      if (product) {
        productName = product.name;
      }

      const productNameAndQuantity = `${selectedProduct.count} X ${productName}`;

      if (!string) {
        string = productNameAndQuantity;
        return;
      }

      if (index === bundledItems.length - 1) {
        string += ` and ${productNameAndQuantity}`;
        stringFinished = true;
        return;
      }

      if (string.length > 30) {
        string += ` and ${bundledItems.length - index} others`;
        stringFinished = true;
      } else {
        string += `, ${productNameAndQuantity}`;
        return;
      }
    });

    return string;
  }, [products, order]);

  const totalCost = useMemo(() => {
    return bundledItems.reduce((runningTotal, currentSelectedProduct) => {
      const currentProduct = products.find(p => p.id === currentSelectedProduct.catalogItem);

      if (!currentProduct?.price) {
        return runningTotal;
      }

      return runningTotal + currentProduct.price.price * currentSelectedProduct.count;
    }, 0);
  }, [products, order]);

  const totalDeviceFee = useMemo(() => {
    return bundledItems.reduce(
      (runningTotal, currentSelectedProduct) => {
        if (runningTotal === undefined) {
          return undefined;
        }

        let newTotal = runningTotal;
        const currentProduct = products.find(p => p.id === currentSelectedProduct.catalogItem);

        if (currentProduct?.category.isMainDevice) {
          const deviceFeePrice = deviceFee?.price?.price ?? 0;
          if (deviceFeePrice) {
            newTotal += currentSelectedProduct.count * deviceFeePrice;
          }
        }

        return newTotal;
      },
      0 as number | undefined
    );
  }, [products, order]);

  const totalPcmCost = useMemo(() => {
    const selectedModel = models ? models[0] : perModel;
    return (totalCost + (totalDeviceFee ?? 0)) * (selectedModel?.pcmFactor ?? 0);
  }, [products, order, models]);

  return {
    products: poProducts,
    productNameString,
    totalCost,
    totalPcmCost,
    totalDeviceFee,
    status: order?.state || PoStatus.Draft,
    models,
    perModel,
    deviceFeeItem: deviceFee
  };
};
